// Downloadable, co-branded map-veto card for the MATCH CARD (Lisa, 2026-07-23).
//
// This is the PORTAL's OWN renderer. It deliberately does NOT reuse the
// gganvil-site /ggveto card (a live competition depends on that site, and the
// hard rule is that nothing here can affect it). The tradeoff is duplication:
// this canvas and the site's are separate implementations that must be kept in
// visual step by hand. It is co-branded the same way — GG Anvil chrome, the
// org's own logo top-right, the org's accent colour — using branding the match
// card resolved server-side (vetoCardBranding) via the SAME shared resolvers
// the site reads, so the two cards show the same identity from the same source.
//
// Fires only once the veto is LOCKED (the caller gates on mapVeto.status).

import { useState } from 'react';
import { Download } from 'lucide-react';

export interface VetoCardPick {
  gameNum: number;
  mode: string;
  mapName: string;
}

interface Props {
  clan1Label: string;
  clan2Label: string;
  compName?: string;
  gameModes: string[];
  picks: VetoCardPick[];
  branding?: { logoUrl?: string; color?: string; brand?: string };
  // In-game faction per side (Lisa, 2026-07-24). Optional — legacy vetoes
  // (locked before the faction gate shipped) have no `factions`, and the
  // card renders exactly as before when it's absent.
  factions?: { clan1: 'JSOC' | 'GUILD'; clan2: 'JSOC' | 'GUILD' };
}

const MODE_LABEL: Record<string, string> = {
  hardpoint: 'Hardpoint',
  snd: 'Search & Destroy',
  overload: 'Overload',
  control: 'Control',
};
function modeLabel(mode: string): string {
  return MODE_LABEL[mode.toLowerCase()] ?? mode.toUpperCase();
}

const HEX = /^#[0-9a-fA-F]{6}$/;
const DEFAULT_ACCENT = '#ff731b'; // GG Anvil forge orange — see gganvil_portal_brand_tokens

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Required before src for the cross-origin org logo (app.goodgameanvil.com);
    // without it the draw taints the canvas and toBlob() throws. No-op for the
    // same-origin /brand assets, so one path covers both. Mirrors the site card.
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

async function drawCard(canvas: HTMLCanvasElement, props: Props): Promise<void> {
  const { clan1Label, clan2Label, compName, gameModes, picks, branding, factions } = props;
  const ctx = canvas.getContext('2d')!;
  const W = canvas.width;   // 1080
  const H = canvas.height;  // 1350
  try { await (document as unknown as { fonts?: { ready: Promise<unknown> } }).fonts?.ready; } catch { /* fonts optional */ }

  const accent = branding?.color && HEX.test(branding.color) ? branding.color : DEFAULT_ACCENT;
  const COL_BG = '#0a0a0f';
  const COL_BG2 = '#111118';
  const COL_TEXT = '#f2f2f5';
  const COL_MUTED = '#7a7a92';
  const COL_FAINT = '#2b2b3a';

  // Background
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, COL_BG);
  bg.addColorStop(1, COL_BG2);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  // Soft accent glow at the top
  const glow = ctx.createRadialGradient(W / 2, 0, 0, W / 2, 0, 820);
  glow.addColorStop(0, `${accent}22`);
  glow.addColorStop(1, `${accent}00`);
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // Frame
  ctx.strokeStyle = COL_FAINT;
  ctx.lineWidth = 2;
  ctx.strokeRect(48, 48, W - 96, H - 96);
  ctx.strokeStyle = `${accent}66`;
  ctx.lineWidth = 1;
  ctx.strokeRect(60, 60, W - 120, H - 120);

  const MARGIN = 110;

  // ── Header band: GG Anvil left, org logo (co-brand) right ──────────────────
  try {
    const wide = await loadImage('/brand/gganvil-wide.svg');
    const h = 44;
    ctx.drawImage(wide, MARGIN, 104, h * (wide.width / wide.height), h);
  } catch { /* logo optional */ }

  if (branding?.logoUrl) {
    try {
      const org = await loadImage(branding.logoUrl);
      const h = 60;
      const w = Math.min(h * (org.width / org.height), 260);
      ctx.drawImage(org, W - MARGIN - w, 96, w, h);
    } catch { /* org logo optional — degrade to GG-Anvil-only */ }
  }

  // ── Title ──────────────────────────────────────────────────────────────────
  ctx.textAlign = 'left';
  ctx.fillStyle = accent;
  ctx.font = '700 26px "IBM Plex Mono", monospace';
  ctx.fillText('MAP VETO', MARGIN, 236);

  ctx.fillStyle = COL_TEXT;
  ctx.font = '900 78px "Big Shoulders Display", "Arial Black", sans-serif';
  ctx.fillText(clan1Label.toUpperCase(), MARGIN, 322);
  const clan1NameWidth = ctx.measureText(clan1Label.toUpperCase()).width;
  ctx.fillStyle = COL_MUTED;
  ctx.font = '900 40px "Big Shoulders Display", "Arial Black", sans-serif';
  ctx.fillText('VS', MARGIN, 382);
  ctx.fillStyle = COL_TEXT;
  ctx.font = '900 78px "Big Shoulders Display", "Arial Black", sans-serif';
  ctx.fillText(clan2Label.toUpperCase(), MARGIN + 96, 382);
  const clan2NameWidth = ctx.measureText(clan2Label.toUpperCase()).width;

  // Faction badges (Lisa, 2026-07-24) — same accent/mono treatment as the
  // mode labels below, set just beside each clan's name so legacy cards
  // (no `factions`) render byte-identical to before.
  if (factions) {
    ctx.textAlign = 'left';
    ctx.fillStyle = accent;
    ctx.font = '700 20px "IBM Plex Mono", monospace';
    ctx.fillText(factions.clan1, Math.min(MARGIN + clan1NameWidth + 20, W - MARGIN - 90), 318);
    ctx.fillText(factions.clan2, Math.min(MARGIN + 96 + clan2NameWidth + 20, W - MARGIN - 90), 378);
  }

  if (compName) {
    ctx.fillStyle = COL_MUTED;
    ctx.font = '400 24px "IBM Plex Mono", monospace';
    ctx.fillText(compName.toUpperCase(), MARGIN, 438);
  }

  // ── Map list ─────────────────────────────────────────────────────────────
  const byGame = new Map<number, VetoCardPick>();
  picks.forEach(p => byGame.set(p.gameNum, p));
  const rows = gameModes.length > 0
    ? gameModes.map((mode, i) => ({ gameNum: i + 1, mode, pick: byGame.get(i + 1) }))
    : picks.slice().sort((a, b) => a.gameNum - b.gameNum).map(p => ({ gameNum: p.gameNum, mode: p.mode, pick: p }));

  const listTop = 500;
  const rowH = Math.min(120, (H - listTop - 190) / Math.max(rows.length, 1));
  rows.forEach((r, i) => {
    const y = listTop + i * rowH;
    ctx.fillStyle = i % 2 === 0 ? '#12121a' : '#0d0d14';
    roundRect(ctx, MARGIN, y, W - MARGIN * 2, rowH - 14, 10);
    ctx.fill();
    // Accent tab
    ctx.fillStyle = accent;
    roundRect(ctx, MARGIN, y, 8, rowH - 14, 4);
    ctx.fill();

    ctx.textAlign = 'left';
    ctx.fillStyle = COL_MUTED;
    ctx.font = '700 24px "IBM Plex Mono", monospace';
    ctx.fillText(`GAME ${r.gameNum}`, MARGIN + 36, y + (rowH - 14) / 2 - 6);
    ctx.fillStyle = accent;
    ctx.font = '700 20px "IBM Plex Mono", monospace';
    ctx.fillText(modeLabel(r.mode).toUpperCase(), MARGIN + 36, y + (rowH - 14) / 2 + 24);

    ctx.textAlign = 'right';
    ctx.fillStyle = r.pick ? COL_TEXT : COL_MUTED;
    ctx.font = '900 52px "Big Shoulders Display", "Arial Black", sans-serif';
    ctx.fillText((r.pick?.mapName ?? 'TBD').toUpperCase(), W - MARGIN - 30, y + (rowH - 14) / 2 + 18);
  });

  // ── Footer ──────────────────────────────────────────────────────────────
  ctx.textAlign = 'center';
  ctx.fillStyle = COL_MUTED;
  ctx.font = '400 22px "IBM Plex Mono", monospace';
  ctx.fillText('GOODGAMEANVIL.COM', W / 2, H - 132);
  try {
    const mark = await loadImage('/brand/gganvil-mark.svg');
    const h = 40;
    ctx.drawImage(mark, W / 2 - (h * (mark.width / mark.height)) / 2, H - 118, h * (mark.width / mark.height), h);
  } catch { /* mark optional */ }
}

export function VetoCardDownload(props: Props) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onDownload = async () => {
    setBusy(true);
    setErr(null);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1350;
      await drawCard(canvas, props);
      const blob = await new Promise<Blob | null>(res => canvas.toBlob(res, 'image/png'));
      if (!blob) throw new Error('Could not render the card');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = `${props.clan1Label}-vs-${props.clan2Label}`.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
      a.download = `gganvil-veto-${safe}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr((e as Error).message || 'Download failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <button
        onClick={onDownload}
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-md bg-forge-600 px-4 py-2 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-forge-500 disabled:opacity-50"
      >
        <Download size={16} /> {busy ? 'Rendering…' : 'Download veto card'}
      </button>
      {err && <span className="font-mono text-xs text-red-400">{err}</span>}
    </div>
  );
}
